export type BlogCategory = 'concept' | 'game' | 'guide';

export interface BlogArticleMeta {
  slug: string;
  title: string;
  shortTitle: string;
  excerpt: string;
  category: BlogCategory;
  iconName: string;
  color: string;
  related: string[];
  /** YYYY-MM-DD. Also used as <lastmod> in the generated sitemap. */
  datePublished: string;
  /** YYYY-MM-DD. Set when a post is substantially updated; overrides datePublished as <lastmod>. */
  dateModified?: string;
}

export const BLOG_ARTICLES: BlogArticleMeta[] = [
  {
    slug: 'how-to-invest',
    title: "How to Invest: A Beginner's Guide to Building Wealth",
    shortTitle: 'How to Invest',
    excerpt: 'Learn the fundamentals of investing, from stocks and ETFs to bonds, and discover how hands-on practice can accelerate your journey.',
    category: 'guide',
    iconName: 'TrendingUp',
    color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    related: ['learn-to-invest', 'how-to-start-investing-with-100-dollars', 'index-funds-vs-stocks'],
    datePublished: '2026-03-20',
  },
  {
    slug: 'learn-to-invest',
    title: 'Learn to Invest: From Financial Concepts to Confident Decisions',
    shortTitle: 'Learn to Invest',
    excerpt: 'Understand key investing concepts like P/E ratios, market cap, and dividends, then put that knowledge into practice.',
    category: 'guide',
    iconName: 'GraduationCap',
    color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    related: ['what-is-pe-ratio', 'what-is-market-cap', 'how-to-read-a-balance-sheet'],
    datePublished: '2026-03-20',
  },
  {
    slug: 'paper-trading',
    title: 'Paper Trading: Practice Investing Without the Risk',
    shortTitle: 'Paper Trading',
    excerpt: 'Discover the benefits of simulated trading and how practicing with virtual money builds real investing confidence.',
    category: 'guide',
    iconName: 'Coins',
    color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
    related: ['how-to-start-investing-with-100-dollars', 'investing-mistakes-beginners-make', 'free-stock-market-games'],
    datePublished: '2026-03-20',
  },
  {
    slug: 'stock-picking',
    title: 'Stock Picking: How to Evaluate and Choose Stocks',
    shortTitle: 'Stock Picking',
    excerpt: 'Learn how to analyze companies using fundamental and technical analysis, avoid common mistakes, and sharpen your stock-picking skills.',
    category: 'guide',
    iconName: 'BarChart3',
    color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400',
    related: ['how-to-value-a-stock', 'how-to-read-a-stock-chart', 'what-is-pe-ratio'],
    datePublished: '2026-03-20',
  },
  {
    slug: 'compare-the-pair',
    title: 'Why We Built Compare the Pair: A Daily Stock Comparison Game',
    shortTitle: 'Compare the Pair',
    excerpt: 'Real investing is about relative comparison. Learn how our daily head-to-head game teaches you to evaluate companies side by side.',
    category: 'game',
    iconName: 'ArrowLeftRight',
    color: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
    related: ['stock-wordle', 'daily-stock-challenge', 'free-stock-market-games'],
    datePublished: '2026-04-17',
  },
  {
    slug: 'trivia-rush',
    title: 'Why We Built Trivia Rush: A Stock Market Higher or Lower Game',
    shortTitle: 'Trivia Rush',
    excerpt: "Most people can't gauge company scale. Trivia Rush builds that intuition with a fast-paced higher-or-lower game using real data.",
    category: 'game',
    iconName: 'Zap',
    color: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
    related: ['compare-the-pair', 'free-stock-market-games', 'what-is-market-cap'],
    datePublished: '2026-04-17',
  },

  // Bucket A: Financial concept explainers
  {
    slug: 'what-is-pe-ratio',
    title: 'What Is a P/E Ratio? (With Examples)',
    shortTitle: 'P/E Ratio',
    excerpt: 'The price-to-earnings ratio in plain English: how to calculate it, what counts as high or low, and how investors actually use it.',
    category: 'concept',
    iconName: 'Calculator',
    color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    related: ['what-is-eps', 'how-to-value-a-stock', 'learn-to-invest'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'what-is-market-cap',
    title: 'What Is Market Capitalization? Large-Cap, Mid-Cap & Small-Cap Explained',
    shortTitle: 'Market Cap',
    excerpt: 'Market cap tells you how big a company really is. Here is the formula, the size tiers, and why it matters for risk.',
    category: 'concept',
    iconName: 'Scale',
    color: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400',
    related: ['what-is-pe-ratio', 'index-funds-vs-stocks', 'trivia-rush'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'what-is-eps',
    title: 'What Is EPS (Earnings Per Share)?',
    shortTitle: 'EPS',
    excerpt: 'EPS is one of the most-quoted numbers in finance. Learn the formula, the difference between basic and diluted EPS, and what a "good" EPS looks like.',
    category: 'concept',
    iconName: 'DollarSign',
    color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    related: ['what-is-pe-ratio', 'revenue-vs-earnings', 'how-to-value-a-stock'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'what-is-dividend-yield',
    title: 'What Is Dividend Yield and How Do You Calculate It?',
    shortTitle: 'Dividend Yield',
    excerpt: 'Dividend yield turns a company\'s payout into a percentage you can compare across stocks. Here is how it works and what to watch out for.',
    category: 'concept',
    iconName: 'Percent',
    color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
    related: ['what-is-eps', 'index-funds-vs-stocks', 'learn-to-invest'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'what-is-roe',
    title: 'What Is Return on Equity (ROE)?',
    shortTitle: 'ROE',
    excerpt: 'ROE measures how efficiently a company turns shareholder money into profit. The formula, sector benchmarks, and the trap to avoid.',
    category: 'concept',
    iconName: 'TrendingUp',
    color: 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400',
    related: ['debt-to-equity-ratio', 'what-is-pe-ratio', 'how-to-read-a-balance-sheet'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'gross-vs-operating-margin',
    title: "Gross Margin vs Operating Margin: What's the Difference?",
    shortTitle: 'Gross vs Operating Margin',
    excerpt: 'Two margins, two very different stories. Learn what each one tells you about a business and how to compare them across sectors.',
    category: 'concept',
    iconName: 'PieChart',
    color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400',
    related: ['revenue-vs-earnings', 'what-is-free-cash-flow', 'stock-picking'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'revenue-vs-earnings',
    title: "Revenue vs Earnings: What's the Difference?",
    shortTitle: 'Revenue vs Earnings',
    excerpt: 'Revenue and earnings get confused all the time. Here is what each really means, why earnings can be misleading, and how to read them together.',
    category: 'concept',
    iconName: 'Receipt',
    color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400',
    related: ['what-is-eps', 'gross-vs-operating-margin', 'how-to-read-a-balance-sheet'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'what-is-free-cash-flow',
    title: 'What Is Free Cash Flow and Why Does It Matter?',
    shortTitle: 'Free Cash Flow',
    excerpt: 'Free cash flow is the money a business actually generates after running and growing itself. The formula, sample calculation, and why investors love it.',
    category: 'concept',
    iconName: 'Wallet',
    color: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400',
    related: ['revenue-vs-earnings', 'gross-vs-operating-margin', 'how-to-value-a-stock'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'debt-to-equity-ratio',
    title: 'What Is the Debt-to-Equity Ratio?',
    shortTitle: 'Debt-to-Equity',
    excerpt: 'D/E ratio measures how much of a company is funded by debt. Learn the formula, sector benchmarks, and the warning signs to watch.',
    category: 'concept',
    iconName: 'Landmark',
    color: 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400',
    related: ['what-is-roe', 'how-to-read-a-balance-sheet', 'investing-mistakes-beginners-make'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'how-to-read-a-balance-sheet',
    title: "How to Read a Balance Sheet (Beginner's Guide)",
    shortTitle: 'Reading a Balance Sheet',
    excerpt: 'A balance sheet is just three sections: assets, liabilities, and equity. Here is how to read one in five minutes without any accounting background.',
    category: 'concept',
    iconName: 'BookOpen',
    color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
    related: ['debt-to-equity-ratio', 'what-is-roe', 'stock-picking'],
    datePublished: '2026-05-04',
  },

  // Bucket B: Game / product SEO
  {
    slug: 'stock-wordle',
    title: 'What Is Stock Wordle? The Daily Stock Guessing Game Explained',
    shortTitle: 'Stock Wordle',
    excerpt: 'Stock Wordle is the daily puzzle for stock market lovers. Here is how it works, why people play, and where to try one for free.',
    category: 'game',
    iconName: 'Puzzle',
    color: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
    related: ['finance-wordle', 'daily-stock-challenge', 'free-stock-market-games'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'finance-wordle',
    title: 'Finance Wordle: A Daily Puzzle for Stock Market Learners',
    shortTitle: 'Finance Wordle',
    excerpt: 'A "finance Wordle" gives you one finance puzzle per day. Here is what makes a good one and how it doubles as a learning tool.',
    category: 'game',
    iconName: 'Calendar',
    color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    related: ['stock-wordle', 'daily-stock-challenge', 'compare-the-pair'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'daily-stock-challenge',
    title: 'What Is a Daily Stock Challenge and How Do You Play One?',
    shortTitle: 'Daily Stock Challenge',
    excerpt: 'A daily stock challenge gives you one stock puzzle each day. Here is the format, the strategy, and how to start one for free.',
    category: 'game',
    iconName: 'Target',
    color: 'bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400',
    related: ['stock-wordle', 'finance-wordle', 'free-stock-market-games'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'free-stock-market-games',
    title: 'The Best Free Stock Market Games to Learn Investing',
    shortTitle: 'Free Stock Market Games',
    excerpt: 'A short, opinionated list of free stock market games that actually teach you something, from daily puzzles to head-to-head trivia.',
    category: 'game',
    iconName: 'Gamepad2',
    color: 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400',
    related: ['paper-trading', 'stock-wordle', 'trivia-rush'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'wsb-stocks',
    title: 'What Stocks Does WallStreetBets Actually Trade?',
    shortTitle: 'WSB Stocks',
    excerpt: 'WallStreetBets has a recognisable list of favourites. Here is what shows up on r/wallstreetbets, why, and how to track it.',
    category: 'game',
    iconName: 'Flame',
    color: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
    related: ['stock-picking', 'investing-mistakes-beginners-make', 'free-stock-market-games'],
    datePublished: '2026-05-04',
  },

  // Bucket C: Beginner action guides
  {
    slug: 'how-to-start-investing-with-100-dollars',
    title: 'How to Start Investing with $100',
    shortTitle: 'Investing With $100',
    excerpt: 'You do not need thousands to start. Here is a realistic step-by-step plan for putting your first $100 into the market this week.',
    category: 'guide',
    iconName: 'Sprout',
    color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    related: ['index-funds-vs-stocks', 'paper-trading', 'investing-mistakes-beginners-make'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'investing-mistakes-beginners-make',
    title: '10 Investing Mistakes Beginners Make (And How to Avoid Them)',
    shortTitle: 'Beginner Mistakes',
    excerpt: 'The 10 mistakes that quietly drain returns for first-time investors, plus a one-line fix for each one.',
    category: 'guide',
    iconName: 'AlertTriangle',
    color: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
    related: ['how-to-start-investing-with-100-dollars', 'paper-trading', 'index-funds-vs-stocks'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'index-funds-vs-stocks',
    title: 'Index Funds vs Individual Stocks: Which Is Better for Beginners?',
    shortTitle: 'Index Funds vs Stocks',
    excerpt: 'Index funds and individual stocks solve different problems. Here is when to use each and how most beginners should mix them.',
    category: 'guide',
    iconName: 'Layers',
    color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    related: ['how-to-start-investing-with-100-dollars', 'how-to-invest', 'investing-mistakes-beginners-make'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'how-to-read-a-stock-chart',
    title: 'How to Read a Stock Chart (With a Worked Example)',
    shortTitle: 'Reading Stock Charts',
    excerpt: 'A stock chart looks intimidating but really only has a few moving parts. Learn the price line, volume, time frames, and what to ignore.',
    category: 'guide',
    iconName: 'LineChart',
    color: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400',
    related: ['how-to-value-a-stock', 'stock-picking', 'stock-wordle'],
    datePublished: '2026-05-04',
  },
  {
    slug: 'how-to-value-a-stock',
    title: 'How to Value a Stock in 5 Minutes (P/E, P/B, PEG)',
    shortTitle: 'How to Value a Stock',
    excerpt: 'You will not build a DCF in 5 minutes, but you can sanity-check a stock with three quick ratios. Here is the workflow.',
    category: 'guide',
    iconName: 'Scale',
    color: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400',
    related: ['what-is-pe-ratio', 'what-is-eps', 'stock-picking'],
    datePublished: '2026-05-04',
  },
];

export const ARTICLES_BY_SLUG: Record<string, BlogArticleMeta> = BLOG_ARTICLES.reduce(
  (acc, article) => {
    acc[article.slug] = article;
    return acc;
  },
  {} as Record<string, BlogArticleMeta>
);

export const CATEGORY_LABELS: Record<BlogCategory, string> = {
  concept: 'Concepts',
  game: 'Games',
  guide: 'Guides',
};
